import useApi from 'common/hooks/useApi';
import formatAddress from 'common/services/address/format-address';
import DraftApplication from 'domain/types/DraftApplication';
import { useCallback, useEffect } from 'react';
import FormVerificationResponse from '../verify-identity/FormVerificationResponse';
import { DetailRow } from '../../../features/group-details/DetailRow';
import useTypedSelector from 'common/hooks/useTypedSelector';
import DwellingTypes from 'domain/enums/DwellingTypes';

interface IApplicationDetails {
  data: DraftApplication;
}

const ApplicationOverview: React.FC<IApplicationDetails> = ({ data }) => {
  const { name } = useTypedSelector((state) => ({
    name: `${state.profile.data.firstName} ${state.profile.data.lastName}`,
  }));

  const [{ data: verification }, fetchStatus] =
    useApi<FormVerificationResponse>({
      url: '',
      method: 'GET',
    });

  const {
    groupRefKey,
    address,
    email,
    dwellingTypeCd,
    howManyRooms,
    propertyRelaDescr,
    intermediaries,
    dwellingTypeDescr,
    numberOfRooms,
    primaryResIndicator,
  } = data;

  const refreshVerification = useCallback(() => {
    if (groupRefKey !== null) {
      fetchStatus(
        undefined,
        `/applications/primary-residence-status?groupRefKey=` + groupRefKey
      );
    }
  }, [fetchStatus, groupRefKey]);

  // Get primary residence status on page load
  useEffect(() => {
    refreshVerification();
  }, [refreshVerification]);

  const hasAttachments =
    verification && verification.primaryResAttachment.length >= 2;

  const attachment1 =
    verification &&
    verification.primaryResAttachment.find(
      (a) => a.uploadType === 'PRIMARY_RESIDENCE'
    );
  const attachment2 =
    verification &&
    verification.primaryResAttachment.find(
      (a) => a.uploadType === 'PRIMARY_RESIDENCE_02'
    );

  const primResDoc =
    data.primaryResIndicator !== 'Y'
      ? []
      : [
          {
            label: "Primary Residency: ID or Driver's License ",
            value: hasAttachments && attachment1 ? attachment1.filename : '--',
          },
          {
            label: 'Primary Residency: Supplementary Document',
            value: hasAttachments && attachment2 ? attachment2.filename : '--',
          },
        ];

  const amountListingRooms =
    dwellingTypeCd === DwellingTypes.COMBINATION ||
    dwellingTypeCd === DwellingTypes.PARTIAL
      ? [
          {
            label: 'Total rooms you are listing',
            value: howManyRooms,
          },
        ]
      : [];

  const rows = [
    {
      label: 'Applicant name',
      value: name,
    },
    {
      label: 'Address',
      value: formatAddress(address),
    },
    {
      label: 'Email',
      value: email,
    },
    {
      label: 'Relationship to property',
      value: propertyRelaDescr,
    },
    {
      label: 'Rental intermediaries',
      value: intermediaries.map((i) => i.descr).join(', '),
    },
    {
      label: 'Dwelling type',
      value: dwellingTypeDescr,
    },
    {
      label: 'Total number of sleeping rooms in unit',
      value: numberOfRooms,
    },
    ...amountListingRooms,
    {
      label: 'Primary residence',
      value:
        primaryResIndicator === 'Y'
          ? 'Yes'
          : primaryResIndicator === 'N'
          ? 'No'
          : '--',
    },
    ...primResDoc,
  ];

  return (
    <>
      {rows.map((row, index) => {
        return (
          <>
            <DetailRow key={index} left={row.label} right={row.value} />
            <hr />
          </>
        );
      })}
    </>
  );
};

export default ApplicationOverview;
